import Head from 'next/head'
import Image from 'next/image'
import Tippy from '@tippyjs/react';
import Typewriter from 'typewriter-effect';
import { useEffect, useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPython, faReact, faNodeJs, faPhp, faLaravel, faAws, faDocker, faGitAlt } from '@fortawesome/free-brands-svg-icons';
import { faTerminal, faBrain, faChartLine, faDatabase, faChartBar, faTrophy, faMedal, faAward, faCertificate, faCheckCircle, faGraduationCap, faUniversity, faFlask, faFlag, faGlobeAfrica, faMusic, faLaptopCode, faFilm, faBookOpen, faUsers, faLightbulb, faRocket, faBullseye, faBalanceScale, faHandshake, faEnvelope, faCode, faChalkboardTeacher, faCar, faPiggyBank, faHandsHelping, faExternalLinkAlt, faInfoCircle, faBug, faNetworkWired, faMicrochip, faLock, faShieldAlt, faWifi, faStar } from '@fortawesome/free-solid-svg-icons';

// Composants optimisés pour le SEO - avec SSR activé quand possible
const Modal = dynamic(() => import("../components/modal"), { 
  ssr: false,
  loading: () => <div className="animate-pulse">Chargement...</div>
});
const ContactForm = dynamic(() => import("../components/ContactForm"), {
  ssr: false,
  loading: () => <div className="animate-pulse">Chargement du formulaire...</div>
});
// CVMenu peut être rendu côté serveur pour le SEO
const CVMenu = dynamic(() => import("../components/dropdown"), { 
  ssr: true 
});

// Contenu statique pour le SEO
import StaticContent from '../components/StaticContent';




export default function Home({ enabled, setEnabled, seo, profile }) {

  const [open, setOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);

  // Optimisation de la fonction de lancement du terminal
  const launchTerminal = useCallback(() => {
    setOpen(true);
  }, []);

  // Fonction pour ouvrir le formulaire de contact
  const openContactForm = useCallback(() => {
    setContactFormOpen(true);
  }, []);

  // Optimisation des animations des cartes avec micro-interactions améliorées
  useEffect(() => {
    const cards = document.querySelectorAll('.card');
    
    const handleMouseOver = (card) => () => {
      card.style.transform = 'rotateY(5deg) scale(1.02)';
      card.style.transition = 'transform 0.3s ease-out';
      card.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.15)';
    };
    
    const handleMouseOut = (card) => () => {
      card.style.transform = 'rotateY(0deg) scale(1)';
      card.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
    };

    cards.forEach(card => {
      const overHandler = handleMouseOver(card);
      const outHandler = handleMouseOut(card);
      
      card.addEventListener('mouseover', overHandler, { passive: true });
      card.addEventListener('mouseout', outHandler, { passive: true });
      
      // Cleanup dans la closure
      card._overHandler = overHandler;
      card._outHandler = outHandler;
    });

    return () => {
      cards.forEach(card => {
        if (card._overHandler) {
          card.removeEventListener('mouseover', card._overHandler);
          card.removeEventListener('mouseout', card._outHandler);
        }
      });
    };
  }, []);



  return (
    <div className="w-full overflow-x-hidden">
      <Head>
        <title>Didi Orlog SOSSOU - Développeur FullStack & Data Scientist</title>
        <meta name="description" content="Développeur FullStack spécialisé en IA et Data Science. Expert React, Node.js, Python, Machine Learning. Solutions web et IA sur mesure à Lomé, Togo." />
        <meta name="keywords" content="développeur fullstack, data scientist, intelligence artificielle, machine learning, react, nodejs, python, développeur web togo, IA, big data, aws, lomé" />
        
        {/* Open Graph spécifique à la page */}
        <meta property="og:title" content="Didi Orlog SOSSOU - Développeur FullStack & Data Scientist" />
        <meta property="og:description" content="Développeur FullStack spécialisé en IA et Data Science" />
        
        {/* Twitter Cards spécifique */}
        <meta name="twitter:title" content="Didi Orlog SOSSOU - Développeur FullStack & Data Scientist" />
        <meta name="twitter:description" content="Développeur FullStack spécialisé en IA et Data Science" />
        
        {/* Contenu statique pour améliorer l'indexation */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
              "@type": "Person",
              "name": "SOSSOU Didi Orlog",
              "jobTitle": "Développeur FullStack & Data Scientist",
              "description": "Développeur fullstack passionné par l'innovation technologique, je me spécialise désormais dans l'intelligence artificielle et la science des données. Mon expertise combine développement web moderne et techniques d'apprentissage automatique, me permettant de créer des solutions complètes allant de l'interface utilisateur aux modèles prédictifs avancés.",
              "url": "https://www.orlogsd.dev",
              "sameAs": [
                "https://www.linkedin.com/in/didi-orlog-sossou",
                "https://github.com/LittleDarkBug",
                "https://www.credly.com/users/didi-orlog-sossou"
              ],
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Lomé",
                "addressCountry": "TG"
              },
              "email": "contact@orlogsd.dev",
              "knowsAbout": [
                "Développement Web FullStack",
                "Intelligence Artificielle", 
                "Machine Learning",
                "Data Science",
                "React.js",
                "Node.js", 
                "Python",
                "Laravel",
                "AWS",
                "JavaScript",
                "TypeScript",
                "PyTorch",
                "TensorFlow"
              ],
              "hasOccupation": {
                "@type": "Occupation",
                "name": "Développeur FullStack & Data Scientist",
                "occupationLocation": {
                  "@type": "City", 
                  "name": "Lomé, Togo"
                },
                "skills": [
                  "Développement Web Frontend et Backend",
                  "Intelligence Artificielle et Machine Learning", 
                  "Science des Données et Analytics",
                  "Architecture de Solutions Cloud",
                  "Développement d'APIs REST",
                  "Optimisation de Performance Web"
                ]
              },
              "alumniOf": [
                {
                  "@type": "EducationalOrganization",
                  "name": "École Polytechnique de Lomé"
                },
                {
                  "@type": "EducationalOrganization", 
                  "name": "UTBM"
                }
              ]
            }
          })}
        </script>
      </Head>
      
      <div className={enabled ? 'bg-black-800/10 rounded-lg w-full h-auto mt-6' : 'bg-white-800/10 rounded-lg w-full h-auto mt-6'}>
        <div className="relative overflow-x-hidden">
          <div className="flex flex-col lg:flex-row justify-between w-full p-4 sm:p-6 lg:px-1 items-center h-full">
            <div className="flex flex-col lg:justify-start justify-center items-center lg:items-start w-full lg:w-2/3">
              <div className="flex items-center w-full justify-center lg:justify-start">
                <h1 className={enabled ? 'text-white text-2xl sm:text-3xl font-semibold flex items-center text-center lg:text-left' : 'text-black text-2xl sm:text-3xl font-semibold flex items-center text-center lg:text-left'}>
                  SOSSOU Didi Orlog
                </h1>
              </div>
              <div className={enabled ? 'text-white/90 text-md content-spacing w-full lg:w-5/6' : 'text-black/50 text-md content-spacing w-full lg:w-5/6'}>

                <div className="text-center lg:text-left">
                  <span className="text-orange-500 py-2 font-normal rounded-md text-sm element-spacing block">
                    <Typewriter
                      className="font-normal rounded-md text-sm w-full"
                      options={{
                        strings: ['FULLSTACK DEVELOPER', 'AI ENTHUSIAST', 'DATA SCIENTIST', 'ML ENGINEER', 'SOFTWARE ENGINEER'],
                        autoStart: true,
                        loop: true,
                        delay: 50,
                        deleteSpeed: 30,
                      }}
                    />
                  </span>
                </div>
                <div className="text-center lg:text-left mt-2">
                  <Tippy content="Stack technique" animation="shift-away" arrow={false}>
                    <span className={`py-1 font-normal rounded-md ` + (enabled ? 'text-white' : 'text-black')}>
                      <FontAwesomeIcon icon={faPython} className="text-[#3776ab] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faReact} className="text-[#61dafb] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faNodeJs} className="text-[#68a063] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faPhp} className="text-[#777bb4] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faLaravel} className="text-[#ff2d20] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faAws} className="text-[#ff9900] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faDocker} className="text-[#2496ed] mr-2 text-lg" />
                      <FontAwesomeIcon icon={faGitAlt} className="text-[#f05032] mr-2 text-lg" />
                    </span>
                  </Tippy>
                </div>
                <div className="mt-3 text-center lg:text-left">
                  <span>
                    Développeur fullstack passionné par l'innovation technologique, je me spécialise désormais dans l'intelligence artificielle 
                    et la science des données. Mon expertise combine développement web moderne et techniques d'apprentissage automatique, 
                    me permettant de créer des solutions complètes allant de l'interface utilisateur aux modèles prédictifs avancés.
                  </span>
                </div>
              </div>
              <div className="w-full flex justify-center lg:justify-start">
                <div className="justify-center items-center mt-2 h-auto hidden md:flex">
                  <div className="transform transition-all duration-200 hover:scale-105">
                    <CVMenu enabled={enabled} />
                  </div>
                  <button className="pulse-button term hover:bg-[#191932]/30 shadow-lg hover:shadow-xl transition-all duration-200 rounded-lg p-3 ml-3 transform hover:scale-105 active:scale-95"
                    onClick={launchTerminal}
                  >
                    <p className="text-md text-black flex items-center">
                      <FontAwesomeIcon icon={faTerminal} className="mr-2 transition-transform duration-200 group-hover:rotate-12" /> Terminal
                    </p>
                  </button>
                </div>
              </div>
            </div>
            {/* Séparateur horizontal visible uniquement sur desktop */}
            <div className="hidden lg:block w-12"></div>
            <div
              className="relative blob mt-6 lg:mt-0 flex-shrink-0"
              data-aos="fade-down-right"
              data-aos-duration="800"
              data-aos-easing="ease-in-out"
              data-aos-offset="60"
            >
              <Image
                src="/assets/profile/rm.webp"
                alt="hero"
                fill
                className="rounded-lg"
                style={{ objectFit: 'contain' }}
                priority
              />
            </div>
          </div>
        </div>
      </div>
      <div className="block md:hidden">
        <div className="flex justify-center items-center mt-6 h-auto">
          <div className="transform transition-all duration-200 hover:scale-105">
            <CVMenu enabled={enabled} />
          </div>
          <button className="pulse-button term hover:bg-[#191932]/30 shadow-lg hover:shadow-xl transition-all duration-200 rounded-lg p-3 ml-3 transform hover:scale-105 active:scale-95"
            onClick={launchTerminal}
          >
            <p className="text-md text-black flex items-center">
              <FontAwesomeIcon icon={faTerminal} className="mr-2" /> Terminal
            </p>
          </button>
        </div>
      </div>



  <div className="section-spacing" id="skills" data-aos="fade-down-right" data-aos-duration="800" data-aos-easing="ease-in-out" data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Domaines d'Expertise</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">

          <div
            className="card m-2 cursor-pointer border border-gray-400 rounded-lg hover:shadow-md hover:border-opacity-0 transform hover:-translate-y-1 transition-all duration-200"
          >
            <div className="m-3">
              <h2 className="text-lg mb-2 font-normal">
                <FontAwesomeIcon icon={faNodeJs} className="text-[#339933] mr-2 text-lg" />
                <FontAwesomeIcon icon={faLaravel} className="text-[#f55247] mr-2 text-lg" />
                <FontAwesomeIcon icon={faPhp} className="text-[#777bb4] mr-2 text-lg" />
                Développement Web
                <span className={"text-sm inline rounded-full px-2 align-top float-right animate-pulse " + (!enabled ? 'text-teal-800 font-mono bg-teal-100' : 'text-white font-mono bg-gray-800')}>FullStack</span>
              </h2>
              <p className={"font-light font-mono text-sm transition-all duration-200 " + (!enabled ? 'text-gray-800 hover:text-gray-900' : 'text-white hover:text-gray-100')}>
                Développement d'applications web complètes avec React/Next.js, Node.js et Laravel. 
                Expertise en architectures modernes, APIs RESTful et interfaces utilisateur responsives.
              </p>
              <div className="flex items-center mt-2">
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-orange-500 rounded-full" style={{ width: '95%' }}>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="card m-2 cursor-pointer border border-gray-400 rounded-lg hover:shadow-md hover:border-opacity-0 transform hover:-translate-y-1 transition-all duration-200"
          >
            <div className="m-3">
              <h2 className="text-lg mb-2 font-normal">
                <FontAwesomeIcon icon={faPython} className="text-[#3776ab] mr-2 text-lg" />
                <FontAwesomeIcon icon={faBrain} className="text-[#ff6b6b] mr-2 text-lg" />
                <FontAwesomeIcon icon={faChartLine} className="text-[#4ecdc4] mr-2 text-lg" />
                Intelligence Artificielle
                <span className={"text-sm inline rounded-full px-2 align-top float-right animate-pulse " + (!enabled ? 'text-teal-800 font-mono bg-teal-100' : 'text-white font-mono bg-gray-800')}> IA & ML</span>
              </h2>
              <p className={"font-light font-mono text-sm transition-all duration-200 " + (!enabled ? 'text-gray-800 hover:text-gray-900' : 'text-white hover:text-gray-100')}>
                Spécialisé en Machine Learning et Deep Learning avec PyTorch, TensorFlow et Scikit-learn. 
                Développement de modèles prédictifs et systèmes intelligents sur AWS SageMaker.
              </p>
              <div className="flex items-center mt-2">
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-orange-500 rounded-full" style={{ width: '85%' }}>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card m-2 cursor-pointer border border-gray-400 rounded-lg hover:shadow-md hover:border-opacity-0 transform hover:-translate-y-1 transition-all duration-200"
          >
            <div className="m-3">
              <h2 className="text-lg mb-2 font-normal">
                <FontAwesomeIcon icon={faDatabase} className="text-[#336791] mr-2 text-lg" />
                <FontAwesomeIcon icon={faChartBar} className="text-[#ff6b6b] mr-2 text-lg" />
                <FontAwesomeIcon icon={faPython} className="text-[#3776ab] mr-2 text-lg" />
                Data Science
                <span className={"text-sm inline rounded-full px-2 align-top float-right animate-pulse " + (!enabled ? 'text-teal-800 font-mono bg-teal-100' : 'text-white font-mono bg-gray-800')}>Analytics & BI</span></h2>
              <p className={"font-light font-mono text-sm transition-all duration-200 " + (!enabled ? 'text-gray-800 hover:text-gray-900' : 'text-white hover:text-gray-100')}>
                Analyse et visualisation de données avec Power BI, Python et XLSTAT. 
                Expertise en collecte, nettoyage et transformation de données pour des insights métier exploitables.
              </p>
              <div className="flex items-center mt-2">
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-orange-500 rounded-full" style={{ width: '80%' }}>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="section-spacing-sm" data-aos="fade-down-right"
      data-aos-duration="800"
      data-aos-easing="ease-in-out"
      data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Technologies Usuelles</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8">
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faReact} className="text-[#61dafb] text-4xl sm:text-5xl" />
            </div>
            <p className={"text-sm sm:text-lg font-semibold content-spacing text-center " + (enabled ? 'text-white' : 'text-black')}>React</p>
            <div className="w-3/4 sm:w-1/2 lg:w-1/4 h-2 sm:h-3 bg-gray-200 rounded-full element-spacing">
              <div className="h-2 sm:h-3 bg-[#15803D] rounded-full" style={{ width: '100%' }}>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faNodeJs} className="text-[#339933] text-4xl sm:text-5xl" />
            </div>
            <p className={"text-sm sm:text-lg font-semibold content-spacing text-center " + (enabled ? 'text-white' : 'text-black')}>Node.js</p>
            <div className="w-3/4 sm:w-1/2 lg:w-1/4 h-2 sm:h-3 bg-gray-200 rounded-full element-spacing">
              <div className="h-2 sm:h-3 bg-[#15803D] rounded-full" style={{ width: '100%' }}>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faPython} className="text-[#3776ab] text-4xl sm:text-5xl" />
            </div>
            <p className={"text-sm sm:text-lg font-semibold content-spacing text-center " + (enabled ? 'text-white' : 'text-black')}>Python</p>
            <div className="w-3/4 sm:w-1/2 lg:w-1/4 h-2 sm:h-3 bg-gray-200 rounded-full element-spacing">
              <div className="h-2 sm:h-3 bg-[#15803D] rounded-full" style={{ width: '100%' }}>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faLaravel} className="text-[#f22e2e] text-4xl sm:text-5xl" />
            </div>
            <p className={"text-sm sm:text-lg font-semibold content-spacing text-center " + (enabled ? 'text-white' : 'text-black')}>Laravel</p>
            <div className="w-3/4 sm:w-1/2 lg:w-1/4 h-2 sm:h-3 bg-gray-200 rounded-full element-spacing">
              <div className="h-2 sm:h-3 bg-[#22C55E] rounded-full" style={{ width: '100%' }}>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faAws} className="text-[#ff9900] text-4xl sm:text-5xl" />
            </div>
            <p className={"text-sm sm:text-lg font-semibold content-spacing text-center " + (enabled ? 'text-white' : 'text-black')}>AWS</p>
            <div className="w-3/4 sm:w-1/2 lg:w-1/4 h-2 sm:h-3 bg-gray-200 rounded-full element-spacing">
              <div className="h-2 sm:h-3 bg-[#22C55E] rounded-full" style={{ width: '100%' }}>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faChartLine} className="text-[#4ecdc4] text-4xl sm:text-5xl" />
            </div>
            <p className={"text-sm sm:text-lg font-semibold content-spacing text-center " + (enabled ? 'text-white' : 'text-black')}>Data Science</p>
            <div className="w-3/4 sm:w-1/2 lg:w-1/4 h-2 sm:h-3 bg-gray-200 rounded-full element-spacing">
              <div className="h-2 sm:h-3 bg-[#BEF264] rounded-full" style={{ width: '100%' }}>
              </div>
            </div>
          </div>
        </div>
        <div className="py-12 px-4">
          <div className="lg:max-w-[356px] md:max-w-[516px] max-w-[343px] mx-auto">
            <div className={`mx-auto px-3 py-4 rounded ${enabled ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="block">
                <div className="flex justify-between items-center gap-x-4 px-8">
                  <div aria-label="one">
                    <div className="flex items-center gap-2 mt-3">
                      <svg
                        width="20"
                        height="8"
                        viewBox="0 0 20 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="20" height="8" rx="4" fill="#FACC15" />
                      </svg>

                      <p className={`text-xs font-medium leading-3 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                        Débutant
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <svg
                        width="20"
                        height="8"
                        viewBox="0 0 20 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="20" height="8" rx="4" fill="#BEF264" />
                      </svg>

                      <p className={`text-xs font-medium leading-3 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                        Intermédiaire
                      </p>
                    </div>
                  </div>
                  <div aria-label="two">
                    <div className="flex items-center gap-2 mt-3">
                      <svg
                        width="20"
                        height="8"
                        viewBox="0 0 20 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="20" height="8" rx="4" fill="#22C55E" />
                      </svg>
                      <p className={`text-xs font-medium leading-3 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                        Avancé
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <svg
                        width="20"
                        height="8"
                        viewBox="0 0 20 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="20" height="8" rx="4" fill="#15803D" />
                      </svg>
                      <p className={`text-xs font-medium leading-3 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                        Expert
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section-spacing-sm"
      data-aos="fade-down-left"
      data-aos-duration="800"
      data-aos-easing="ease-in-out"
      data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Certifications et Distinctions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 content-spacing">
          {/* Distinctions */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-xl font-semibold mb-4 flex items-center ${enabled ? 'text-white' : 'text-gray-800'}`}>
              <FontAwesomeIcon icon={faTrophy} className="text-yellow-500 mr-3 text-2xl" />
              Distinctions
            </h3>
            <div className="space-y-3">
              <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faMedal} className="text-orange-500 mr-3 mt-1" />
                <div>
                  <p className="font-semibold">Lauréat bourse d'excellence académique</p>
                  <p className="text-sm">Fondation Nana Anè XV - Première promotion</p>
                </div>
              </div>
              <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faAward} className="text-orange-500 mr-3 mt-1" />
                <div>
                  <p className="font-semibold">6ème place Challenge IA</p>
                  <p className="text-sm">Prédiction adoption fibre optique - MENTD/ATD/Zindi</p>
                </div>
              </div>
            </div>
          </div>

          {/* Certifications récentes */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-xl font-semibold mb-4 flex items-center ${enabled ? 'text-white' : 'text-gray-800'}`}>
              <FontAwesomeIcon icon={faCertificate} className="text-blue-500 mr-3 text-2xl" />
              Certifications Récentes
            </h3>
            <div className="space-y-3">
              <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                <div>
                  <p className="font-semibold">DataCamp Data Scientist Certification</p>
                  <p className="text-sm">2025</p>
                </div>
              </div>
              <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                <div>
                  <p className="font-semibold">DataCamp Data Engineer Certification</p>
                  <p className="text-sm">2025</p>
                </div>
              </div>
              <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                <div>
                  <p className="font-semibold">Machine Learning Fundamentals Nanodegree</p>
                  <p className="text-sm">Udacity - 2024</p>
                </div>
              </div>
              <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                <div>
                  <p className="font-semibold">AI Programming with Python Nanodegree</p>
                  <p className="text-sm">Udacity - 2023</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formation en cours */}
        <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${
          enabled ? 'bg-gradient-to-r from-gray-800 to-gray-700 border border-gray-600' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 flex items-center ${enabled ? 'text-white' : 'text-gray-800'}`}>
            <FontAwesomeIcon icon={faGraduationCap} className="text-blue-500 mr-3 text-2xl" />
            Formation en cours
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
              <FontAwesomeIcon icon={faUniversity} className="text-blue-500 mr-3 mt-1" />
              <div>
                <p className="font-semibold">Master 1 IA et Big Data</p>
                <p className="text-sm">École Polytechnique de Lomé & UTBM</p>
                <p className="text-xs text-orange-500">Octobre 2024 - En cours</p>
              </div>
            </div>
            <div className={`flex items-start ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
              <FontAwesomeIcon icon={faFlask} className="text-purple-500 mr-3 mt-1" />
              <div>
                <p className="font-semibold">Applied Data Science Lab</p>
                <p className="text-sm">WorldQuant University</p>
                <p className="text-xs text-orange-500">Août 2024 - En cours</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Langues */}
      <div className="section-spacing-sm"
      data-aos="fade-up-right"
      data-aos-duration="800"
      data-aos-easing="ease-in-out"
      data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Langues</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Français */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center mb-4">
              <FontAwesomeIcon icon={faFlag} className="text-blue-500 mr-3 text-2xl" />
              <h3 className={`text-xl font-semibold ${enabled ? 'text-white' : 'text-gray-800'}`}>
                Français
              </h3>
            </div>
            <p className={`text-sm mb-3 ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>Langue officielle - maternelle</p>
            <div className="w-full bg-gray-300 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{width: '100%'}}></div>
            </div>
            <p className={`text-xs mt-2 ${enabled ? 'text-gray-400' : 'text-gray-500'}`}>Natif</p>
          </div>

          {/* Anglais */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center mb-4">
              <FontAwesomeIcon icon={faFlag} className="text-red-500 mr-3 text-2xl" />
              <h3 className={`text-xl font-semibold ${enabled ? 'text-white' : 'text-gray-800'}`}>
                Anglais
              </h3>
            </div>
            <p className={`text-sm mb-3 ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>Professionnel</p>
            <div className="w-full bg-gray-300 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{width: '85%'}}></div>
            </div>
            <p className={`text-xs mt-2 ${enabled ? 'text-gray-400' : 'text-gray-500'}`}>B2/C1 - Courant</p>
          </div>

          {/* Ewé */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center mb-4">
              <FontAwesomeIcon icon={faGlobeAfrica} className="text-orange-500 mr-3 text-2xl" />
              <h3 className={`text-xl font-semibold ${enabled ? 'text-white' : 'text-gray-800'}`}>
                Mina / Ewé
              </h3>
            </div>
            <p className={`text-sm mb-3 ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>Langue Maternelle</p> 
            <div className="w-full bg-gray-300 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{width: '100%'}}></div>
            </div>
            <p className={`text-xs mt-2 ${enabled ? 'text-gray-400' : 'text-gray-500'}`}>Natif</p>
          </div>
        </div>
      </div>

      {/* Section Loisirs */}
      <div className="section-spacing-sm"
      data-aos="fade-up-left"
      data-aos-duration="800"
      data-aos-easing="ease-in-out"
      data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Loisirs & Passions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Musique */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 text-center ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <FontAwesomeIcon icon={faMusic} className="text-purple-500 text-4xl mb-4" />
            <h3 className={`text-lg font-semibold mb-2 ${enabled ? 'text-white' : 'text-gray-800'}`}>
              Musique
            </h3>
            <p className={`text-sm ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
              Classique, Rap français, Afrobeat, Pop, Jazz, Rock
            </p>
          </div>

          {/* Tech */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 text-center ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <FontAwesomeIcon icon={faLaptopCode} className="text-blue-500 text-4xl mb-4" />
            <h3 className={`text-lg font-semibold mb-2 ${enabled ? 'text-white' : 'text-gray-800'}`}>
              Technologie
            </h3>
            <p className={`text-sm ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
              Veille technologique, Open Source
            </p>
          </div>

          {/* Manga / Animes */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 text-center ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <FontAwesomeIcon icon={faFilm} className="text-pink-500 text-4xl mb-4" /> 
            <h3 className={`text-lg font-semibold mb-2 ${enabled ? 'text-white' : 'text-gray-800'}`}>
              Manga / Animes
            </h3>
            <p className={`text-sm ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
              Shonen , Seinen, Isekai, Slice of Life, Action
            </p>
          </div>

          {/* Lecture */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 text-center ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <FontAwesomeIcon icon={faBookOpen} className="text-orange-500 text-4xl mb-4" />
            <h3 className={`text-lg font-semibold mb-2 ${enabled ? 'text-white' : 'text-gray-800'}`}>
              Lecture
            </h3>
            <p className={`text-sm ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
             Revues Scientifiques, Sci-fi, Tech, Développement personnel
            </p>
          </div>
        </div>
      </div>

      {/* Section Soft Skills */}
      <div className="section-spacing-sm"
      data-aos="fade-down"
      data-aos-duration="800"
      data-aos-easing="ease-in-out"
      data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Soft Skills</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Compétences interpersonnelles */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-xl font-semibold mb-4 flex items-center ${enabled ? 'text-white' : 'text-gray-800'}`}>
              <FontAwesomeIcon icon={faUsers} className="text-blue-500 mr-3 text-2xl" />
              Compétences Interpersonnelles
            </h3>
            <div className="space-y-3">
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Communication efficace</span>
              </div>
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Travail en équipe</span>
              </div>
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Leadership collaboratif</span>
              </div>
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Empathie et écoute active</span>
              </div>
            </div>
          </div>

          {/* Compétences professionnelles */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-xl font-semibold mb-4 flex items-center ${enabled ? 'text-white' : 'text-gray-800'}`}>
              <FontAwesomeIcon icon={faBrain} className="text-purple-500 mr-3 text-2xl" />
              Compétences Professionnelles
            </h3>
            <div className="space-y-3">
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Résolution de problèmes</span>
              </div>
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Pensée analytique</span>
              </div>
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Adaptabilité</span>
              </div>
              <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                <span>Gestion du temps</span>
              </div>
            </div>
          </div>

          {/* Qualités personnelles */}
          <div className={`p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl md:col-span-2 ${
            enabled ? 'bg-gradient-to-r from-gray-800 to-gray-700 border border-gray-600' : 'bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200'
          }`}>
            <h3 className={`text-xl font-semibold mb-4 flex items-center ${enabled ? 'text-white' : 'text-gray-800'}`}>
              <FontAwesomeIcon icon={faStar} className="text-orange-500 mr-3 text-2xl" />
              Qualités Personnelles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mr-3" />
                  <span>Créativité et innovation</span>
                </div>
                <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faRocket} className="text-blue-500 mr-3" />
                  <span>Proactivité</span>
                </div>
                <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faBullseye} className="text-red-500 mr-3" />
                  <span>Orientation résultats</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faBalanceScale} className="text-green-500 mr-3" />
                  <span>Rigueur et précision</span>
                </div>
                <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faGraduationCap} className="text-purple-500 mr-3" />
                  <span>Apprentissage continu</span>
                </div>
                <div className={`flex items-center ${enabled ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faHandshake} className="text-orange-500 mr-3" />
                  <span>Esprit collaboratif</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

            <div
        className="section-spacing-sm"
        data-aos="fade-down-left"
        data-aos-duration="800"
        data-aos-easing="ease-in-out"
        data-aos-offset="60"
      >
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Badges numériques</h2>
        
        <div className="w-full max-w-6xl mx-auto">
          {/* Section Badges Credly */}
          <div className={`rounded-lg overflow-hidden shadow-lg transition-all duration-300 ${
            enabled ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className={`px-4 py-3 border-b ${
              enabled ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-red-500' : 'bg-red-400'}`}></div>
                  <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-yellow-500' : 'bg-yellow-400'}`}></div>
                  <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-green-500' : 'bg-green-400'}`}></div>
                </div>
                <p className={`text-sm font-mono ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                  credly.com/users/didi-orlog-sossou
                </p>
                <button 
                  onClick={() => window.open('https://www.credly.com/users/didi-orlog-sossou/badges', '_blank')}
                  className={`text-sm px-3 py-1 rounded transition-all duration-200 hover:scale-105 ${
                    enabled 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                  Voir tous les badges
                </button>
              </div>
            </div>
            
            {/* Galerie de badges */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                
                {/* AWS Educate Machine Learning Foundations */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-orange-400 to-yellow-500 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faAws} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      AWS ML Foundations
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      AWS • Juin 2025
                    </p>
                  </div>
                </div>

                {/* Applied Data Science with Python - Level 2 */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faPython} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Applied Data Science Level 2
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Data Analysis Using Python */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faChartLine} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Data Analysis Python
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Data Visualization Using Python */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faChartBar} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Data Visualization Python
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Deep Learning using TensorFlow */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faBrain} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Deep Learning TensorFlow
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Introduction to Cybersecurity */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Introduction Cybersecurity
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      Cisco • Nov. 2020
                    </p>
                  </div>
                </div>

                {/* Fortinet Cybersecurity Fundamentals */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faLock} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Fortinet Cybersecurity
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      Fortinet • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Introduction to IoT */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faWifi} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Introduction to IoT
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      Cisco • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Python for Data Science */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faPython} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Python for Data Science
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Deep Learning Essentials */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faNetworkWired} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Deep Learning Essentials
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

                {/* Cyber Threat Management */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faBug} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      Cyber Threat Management
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      Cisco • Oct. 2022
                    </p>
                  </div>
                </div>

                {/* Accelerated Deep Learning with GPU */}
                <div className={`group p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                  enabled ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faMicrochip} className="text-white text-2xl" />
                    </div>
                    <h4 className={`text-sm font-semibold mb-1 ${enabled ? 'text-white' : 'text-gray-800'}`}>
                      GPU Deep Learning
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-gray-300' : 'text-gray-600'}`}>
                      IBM • Nov. 2022
                    </p>
                  </div>
                </div>

              </div>
              
              {/* Bouton pour voir plus */}
              <div className="text-center mt-6">
                <button
                  onClick={() => window.open('https://www.credly.com/users/didi-orlog-sossou/badges', '_blank')}
                  className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 ${
                    enabled 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                  Voir tous mes badges sur Credly
                </button>
              </div>
            </div>
            
            <div className={`px-4 py-3 border-t text-center ${
              enabled ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
            }`}>
              <p className={`text-sm ${enabled ? 'text-gray-400' : 'text-gray-600'}`}>
                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                Cliquez sur les badges pour plus de détails ou visitez mon profil Credly complet
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="section-spacing"
      data-aos="fade-down-left"
      data-aos-duration="800"
      data-aos-easing="ease-in-out"
      data-aos-offset="60">
        <h2 className={"text-3xl font-semibold section-title-spacing " + (enabled ? 'text-white' : 'text-black')}>
          Mon parcours professionnel</h2>
        <div className="mt-5">
          <ol className="relative border-l border-gray-400 ml-3">

            <li className="mb-10 ml-4">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Juillet 2023 - Avril 2024</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>Ingénieur Logiciel FullStack - Bidiffy (BIDIF SARL)</h3>
              <p className={"mb-4 text-base font-normal " + (enabled ? 'text-white' : 'text-gray-800')}>
                {enabled && <FontAwesomeIcon icon={faCode} className="mr-2" />}
                Principal développeur backend, et dev frontend sur <a href="https://www.draavis.com/fr" target="_blank" className="text-orange-500 hover:text-orange-600">draavis.com</a>, 
                un SaaS de location de véhicules. Stack JavaScript/TypeScript.
              </p>
            </li>
            <li className="ml-4 mb-10">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Mai 2023 - Janvier 2024</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>Professeur particulier d'informatique - Voscours.fr</h3>
              <p className={"mb-4 text-base font-normal " + (enabled ? 'text-white' : 'text-gray-800')}>
                {enabled && <FontAwesomeIcon icon={faChalkboardTeacher} className="mr-2" />}
                Assistance de lycéens/collégiens en informatique à travers la plateforme voscours.fr
              </p>
            </li>
            <li className="ml-4 mb-10">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Novembre 2021 - Juin 2023</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>Consultant Ingénieur logiciel - Ambassade d'Angola au Togo</h3>
              <p className={"mb-4 text-base font-normal " + (enabled ? 'text-white' : 'text-gray-800')}>
                {enabled && <FontAwesomeIcon icon={faLaptopCode} className="mr-2" />}
                Développement de solutions adaptées aux besoins spécifiques du client. Amélioration des processus organisationnels.
              </p>
            </li>
            <li className="ml-4 mb-10">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Novembre 2022 - Février 2023</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>
                Stagiaire Développeur DSI - <a href="https://www.togo-port.net" target="_blank" rel="noreferrer" className="text-orange-500 hover:text-orange-600">Port Autonome de Lomé</a>
              </h3>
              <p className={"mb-4 text-base font-normal " + (enabled ? 'text-white' : 'text-gray-800')}>
                {enabled && <FontAwesomeIcon icon={faCar} className="mr-2" />}
                Développement d'un système de gestion des entrées et sorties des véhicules au PVO
              </p>
            </li>
            <li className="ml-4 mb-10">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Août 2022 - Décembre 2022</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>Stagiaire Génie Logiciel - BERIIA</h3>
              <p className={"mb-4 text-base font-normal " + (enabled ? 'text-white' : 'text-gray-800')}>
                {enabled && <FontAwesomeIcon icon={faPiggyBank} className="mr-2" />}
                Développement d'une plateforme de cagnottes et tontines en ligne (Projet de fin d'études)
              </p>
            </li>
            <li className="ml-4 mb-10">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Juillet 2020 - Aujourd'hui</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>Développeur Web - ENT-Togo</h3>
              <p className={"mb-4 text-base font-normal " + (enabled ? 'text-white' : 'text-gray-800')}>
                {enabled && <FontAwesomeIcon icon={faHandsHelping} className="mr-2" />}
                Bénévolat sur les projets de l'association.
              </p>
            </li>
            <li className="ml-4 mb-10">
              <div className="absolute w-3 h-3 bg-orange-500 rounded-full mt-1.5 -left-1.5 border border-white"></div>
              <time className="mb-1 text-sm font-normal leading-none text-orange-500">Janvier 2023 - Aujourd'hui</time>
              <h3 className={"text-lg font-semibold " + (enabled ? 'text-white' : 'text-black')}>Développement Fullstack Freelance</h3>
              <p className={"text-base font-normal " + (enabled ? 'text-white' : 'text-black')}>
                Développement d'applications web et solutions IA personnalisées.
              </p>
              <button className="bg-orange-400 hover:bg-orange-500 text-white font-bold py-2 px-4 mt-2 rounded transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg"
                onClick={openContactForm}
              >
                <FontAwesomeIcon icon={faEnvelope} className="mr-2 transition-transform duration-200 hover:rotate-12" />
                Me contacter
              </button>
            </li>
          </ol>
        </div>
      </div>
      <Modal open={open} setOpen={setOpen} enabled={enabled} />
      <ContactForm 
        enabled={enabled} 
        isOpen={contactFormOpen} 
        onClose={() => setContactFormOpen(false)} 
      />
    </div>
  )
}

export async function getStaticProps() {
  // Pré-générer les données côté serveur pour le SEO
  const pageData = {
    enabled: true,
    seo: {
      title: "Didi Orlog SOSSOU - Développeur FullStack & Data Scientist spécialisé en IA | Lomé, Togo",
      description: "Développeur FullStack passionné par l'innovation technologique, spécialisé en intelligence artificielle et data science. Expert React, Node.js, Python, Machine Learning. Solutions web et IA sur mesure à Lomé, Togo.",
      keywords: "développeur fullstack, data scientist, intelligence artificielle, machine learning, react, nodejs, python, développeur web togo, IA, big data, aws, laravel, lomé, solutions web, freelance",
    },
    profile: {
      name: "SOSSOU Didi Orlog",
      title: "Développeur FullStack & Data Scientist",
      specialties: ["FULLSTACK DEVELOPER", "AI ENTHUSIAST", "DATA SCIENTIST", "ML ENGINEER", "SOFTWARE ENGINEER"],
      description: "Développeur fullstack passionné par l'innovation technologique, je me spécialise désormais dans l'intelligence artificielle et la science des données. Mon expertise combine développement web moderne et techniques d'apprentissage automatique, me permettant de créer des solutions complètes allant de l'interface utilisateur aux modèles prédictifs avancés."
    },
    // Pré-charger les données pour éviter le rendu côté client
    lastUpdated: new Date().toISOString(),
  }

  return {
    props: pageData,
    // Régénérer la page toutes les 24h
    revalidate: 86400,
  }
}